<template>
    <div class="max-w-4xl mx-auto space-y-8 pb-20">
        <!-- HEADER / RESUME -->
        <div class="bg-gradient-to-br from-gray-900 to-slate-800 rounded-3xl p-6 text-white shadow-xl shadow-slate-200">
            <div class="flex items-center justify-between mb-4">
                <div class="flex items-center gap-3">
                    <div class="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-md">
                        <i class="fas fa-file-contract text-2xl" :class="validation.errors.length ? 'text-red-500 animate-pulse' : 'text-emerald-400'"></i>
                    </div>
                    <div>
                        <h2 class="text-xl font-bold tracking-tight">CERFA 15497*04</h2>
                        <p class="text-xs font-medium" :class="validation.errors.length ? 'text-red-400' : 'text-slate-400'">
                            {{ validation.errors.length ? 'Données invalides' : 'Fiche d\'intervention fluides' }}
                        </p>
                    </div>
                </div>
                <div class="text-right">
                    <div class="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Impact Carbone</div>
                    <div class="text-2xl font-black text-red-400">
                        {{ form.fluide_tonnage_eq_co2 || 0 }} <span class="text-xs font-bold text-slate-400">t CO₂eq</span>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-2 gap-3 mt-6">
                <div class="bg-white/5 rounded-2xl p-4 backdrop-blur-sm border border-white/5">
                    <div class="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Fréquence de contrôle</div>
                    <div class="text-sm font-bold text-white flex items-center gap-2">
                        <i class="fas fa-clock text-red-400"></i>
                        <template v-if="!showManualFrequency">
                            {{ leakCheckFrequencyText }}
                        </template>
                        <template v-else>
                            <span class="text-orange-400 animate-pulse">Saisie manuelle requise</span>
                        </template>
                    </div>
                </div>
                <div class="bg-white/5 rounded-2xl p-4 backdrop-blur-sm border border-white/5">
                    <div class="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Catégorie Fluide</div>
                    <div class="text-sm font-bold text-white flex items-center gap-2">
                        <i class="fas fa-vial text-red-400"></i>
                        {{ selectedFluide ? selectedFluide.type : 'Non défini' }}
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION 1: ÉQUIPEMENT & FLUIDE -->
        <div class="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 space-y-6">
            <div class="flex items-center gap-2 mb-2">
                <div class="w-1.5 h-6 bg-red-500 rounded-full"></div>
                <h3 class="font-bold text-lg text-gray-900">Équipement & Fluide</h3>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <!-- Autocomplete fluide -->
                <div class="space-y-2">
                    <label class="text-xs font-black uppercase tracking-widest text-gray-400">
                        <span class="text-red-500 mr-0.5">*</span>Fluide utilisé
                    </label>
                    <div class="relative group">
                        <i class="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-red-500 transition-colors"></i>
                        <input
                            v-model="search"
                            @input="filterFluides"
                            @focus="showDropdown = true"
                            type="text"
                            placeholder="Rechercher un fluide (ex: R32...)"
                            :class="showErrors && (!form.fluide_denomination || !selectedFluide) ? 'border-red-500 bg-red-50' : 'border-transparent bg-gray-50'"
                            class="w-full pl-11 pr-4 py-3 border rounded-2xl text-sm font-bold focus:bg-white focus:border-red-500 focus:ring-4 focus:ring-red-500/10 transition-all outline-none"
                        />
                        <ul v-if="showDropdown && filteredFluides.length" class="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 max-h-60 overflow-y-auto py-2">
                            <li
                                v-for="fluide in filteredFluides"
                                :key="fluide.code"
                                @mousedown.prevent="selectFluide(fluide)"
                                class="px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                                <div class="flex justify-between items-center">
                                    <span class="font-black text-gray-900">{{ fluide.code }}</span>
                                    <span class="text-[10px] font-bold px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">{{ fluide.type }}</span>
                                </div>
                                <div class="text-xs text-gray-400 mt-1">{{ fluide.nom }}</div>
                            </li>
                        </ul>
                    </div>
                </div>

                <!-- Charge Totale -->
                <div class="space-y-2">
                    <label class="text-xs font-black uppercase tracking-widest text-gray-400">
                        <span class="text-red-500 mr-0.5">*</span>Charge totale initiale (kg)
                    </label>
                    <div class="relative">
                        <input
                            type="number"
                            step="0.01"
                            v-model.number="form.fluide_charge_totale_kg"
                            placeholder="0.00"
                            :class="showErrors && (form.fluide_charge_totale_kg === null || form.fluide_charge_totale_kg === '') ? 'border-red-500 bg-red-50' : 'border-transparent bg-gray-50'"
                            class="w-full px-4 py-3 border rounded-2xl text-sm font-bold focus:bg-white focus:border-red-500 outline-none"
                        />
                        <span class="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">kg</span>
                    </div>
                </div>

                <!-- PRG Manuel (Si manquant) -->
                <div v-if="selectedFluide && selectedFluide.prg === null" class="space-y-2 animate-in slide-in-from-left-2">
                    <label class="text-xs font-black uppercase tracking-widest text-orange-500 flex items-center gap-1">
                        <span class="text-red-500">*</span><i class="fas fa-exclamation-circle"></i> PRG Manquant - Saisir PRG
                    </label>
                    <input
                        type="number"
                        v-model.number="form.fluide_prg_manuel"
                        placeholder="Ex: 2088"
                        class="w-full px-4 py-3 bg-orange-50 border border-orange-200 rounded-2xl text-sm font-bold focus:bg-white focus:border-orange-500 outline-none"
                    />
                </div>
            </div>

            <!-- Détecteur permanent -->
            <div class="p-4 bg-red-50 rounded-2xl border border-red-100 flex items-center justify-between">
                <div class="flex items-center gap-3">
                    <div class="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                        <i class="fas fa-bullseye text-red-500"></i>
                    </div>
                    <div>
                        <div class="text-sm font-bold text-gray-900">Système permanent de détection</div>
                        <div class="text-[10px] text-red-600 font-bold uppercase">Réduit la fréquence de contrôle</div>
                    </div>
                </div>
                <label class="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" v-model="form.systeme_detection_present" class="sr-only peer">
                    <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                </label>
            </div>
        </div>

        <!-- Fréquence Manuelle (Si calcul impossible) -->
        <div v-if="showManualFrequency" class="p-6 bg-red-50 rounded-3xl border border-red-100 space-y-3 animate-in slide-in-from-bottom-2">
            <div class="flex items-center gap-2 text-red-600">
                <i class="fas fa-clock"></i>
                <h3 class="font-black text-sm uppercase tracking-widest text-red-700">
                    <span class="text-red-500 mr-1">*</span>Fréquence de contrôle manuelle
                </h3>
            </div>
            <p class="text-xs font-bold text-red-600/70">
                La réglementation n'a pas pu déterminer automatiquement la fréquence pour ce type de fluide. Veuillez l'indiquer manuellement.
            </p>
            <input
                v-model="form.frequence_manuelle"
                placeholder="Ex: 12 mois, 6 mois, Non obligatoire..."
                :class="showErrors && !form.frequence_manuelle ? 'border-red-500' : 'border-red-200'"
                class="w-full px-4 py-3 bg-white border rounded-2xl text-sm font-bold focus:border-red-500 outline-none transition-all placeholder:text-red-200 text-red-700"
            />
        </div>

        <!-- SECTION 2: NATURE DE L'INTERVENTION -->
        <div class="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 space-y-6">
            <div class="flex items-center gap-2 mb-2">
                <div class="w-1.5 h-6 bg-orange-500 rounded-full"></div>
                <h3 class="font-bold text-lg text-gray-900"><span class="text-red-500 mr-1">*</span>Nature de l'intervention</h3>
            </div>

            <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <button
                    v-for="(label, key) in interventionLabels"
                    :key="key"
                    type="button"
                    @click="form[key] = !form[key]"
                    :class="form[key] ? 'bg-orange-500 text-white shadow-lg shadow-orange-200' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'"
                    class="p-4 rounded-2xl transition-all duration-200 text-left group"
                >
                    <i :class="interventionIcons[key]" class="mb-2 text-sm block"></i>
                    <span class="text-xs font-bold leading-tight">{{ label }}</span>
                </button>

                <!-- Autre intervention -->
                <button
                    type="button"
                    @click="form.intervention_autre = !form.intervention_autre"
                    :class="form.intervention_autre ? 'bg-slate-800 text-white' : 'bg-gray-50 text-gray-500'"
                    class="p-4 rounded-2xl transition-all duration-200 text-left"
                >
                    <i class="fas fa-ellipsis-h mb-2 text-sm block"></i>
                    <span class="text-xs font-bold">Autre...</span>
                </button>
            </div>

            <div v-if="form.intervention_autre" class="animate-in fade-in slide-in-from-top-2 duration-300 relative">
                <textarea
                    v-model="form.intervention_autre_precisez"
                    rows="2"
                    maxlength="30"
                    placeholder="Précisez la nature de l'intervention..."
                    class="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-2xl text-sm font-medium focus:bg-white focus:border-slate-800 outline-none pr-16"
                ></textarea>
                <div class="absolute right-4 bottom-3 text-[10px] font-black tracking-widest" :class="form.intervention_autre_precisez.length >= 30 ? 'text-red-500' : 'text-slate-400'">
                    {{ form.intervention_autre_precisez.length }} / 30
                </div>
            </div>
        </div>

        <!-- SECTION 3: CONTRÔLE D'ÉTANCHÉITÉ & FUITES -->
        <div class="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 space-y-6">
            <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                    <div class="w-1.5 h-6 bg-blue-500 rounded-full"></div>
                    <h3 class="font-bold text-lg text-gray-900">Contrôle d'étanchéité</h3>
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="space-y-2">
                    <label class="text-xs font-black uppercase tracking-widest text-gray-400">
                        <span v-if="form.controle_etancheite_periodique || form.controle_etancheite_non_periodique" class="text-red-500 mr-0.5">*</span>Date du contrôle
                    </label>
                    <input type="date" v-model="form.controle_etancheite_date" class="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-2xl text-sm font-bold outline-none focus:bg-white focus:border-blue-500">
                </div>
                <div class="space-y-2">
                    <label class="text-xs font-black uppercase tracking-widest text-gray-400">
                        <span v-if="form.controle_etancheite_periodique || form.controle_etancheite_non_periodique" class="text-red-500 mr-0.5">*</span>ID Détecteur Manuel
                    </label>
                    <input type="text" v-model="form.controle_etancheite_identification" placeholder="Ex: AE-450-Z" class="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-2xl text-sm font-bold outline-none focus:bg-white focus:border-blue-500">
                </div>
            </div>

            <div class="flex items-center gap-2">
                <label class="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" v-model="form.fuite_oui_non" class="sr-only peer">
                    <div class="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                </label>
                <span class="text-sm font-bold text-gray-400">Fuite détectée ?</span>
            </div>

            <!-- FUITES (Conditionnel) -->
            <div v-if="form.fuite_oui_non" class="space-y-4 animate-in zoom-in-95 duration-300">
                <div v-for="n in 3" :key="n" class="p-4 bg-blue-50 rounded-2xl border border-blue-100 space-y-3">
                    <div class="flex items-center gap-2 text-blue-600">
                        <span class="text-red-500 text-xs">*</span><i class="fas fa-search-location text-xs"></i>
                        <span class="text-xs font-black uppercase tracking-widest">Fuite #{{ n }}</span>
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input v-model="form[`fuite_${n}_localisation`]" placeholder="Localisation (ex: Raccord compresseur)" :class="showErrors && form.fuite_oui_non && !form.fuite_1_localisation && !form.fuite_2_localisation && !form.fuite_3_localisation ? 'border-red-500' : 'border-blue-100'" class="w-full px-4 py-2 bg-white rounded-xl text-xs font-bold border outline-none focus:border-blue-500">
                        <select v-model="form[`fuite_${n}_reparation`]" :class="showErrors && form.fuite_oui_non && form[`fuite_${n}_localisation`] && (form[`fuite_${n}_reparation`] === 0) ? 'border-red-500' : 'border-blue-100'" class="w-full px-4 py-2 bg-white rounded-xl text-xs font-bold border outline-none focus:border-blue-500">
                            <option :value="0">Non concerné</option>
                            <option :value="1">Réparée immédiatement</option>
                            <option :value="2">À réparer ultérieurement</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION 4: CHARGE DE FLUIDE -->
        <div class="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 space-y-6">
            <div class="flex items-center gap-2 mb-2">
                <div class="w-1.5 h-6 bg-emerald-500 rounded-full"></div>
                <h3 class="font-bold text-lg text-gray-900">Charge de fluide par l'opérateur</h3>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div class="space-y-2">
                    <label class="text-[10px] font-black uppercase tracking-widest text-gray-400">Fluide Vierge (kg)</label>
                    <input type="number" step="0.01" v-model.number="form.qtt_charge_fluide_vierge" class="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-2xl text-sm font-bold outline-none focus:bg-white focus:border-emerald-500">
                </div>
                <div class="space-y-2">
                    <label class="text-[10px] font-black uppercase tracking-widest text-gray-400">Fluide Recyclé (kg)</label>
                    <input type="number" step="0.01" v-model.number="form.qtt_charge_fluide_recycle" class="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-2xl text-sm font-bold outline-none focus:bg-white focus:border-emerald-500">
                </div>
                <div class="space-y-2">
                    <label class="text-[10px] font-black uppercase tracking-widest text-gray-400">Fluide Régénéré (kg)</label>
                    <input type="number" step="0.01" v-model.number="form.qtt_charge_fluide_regenere" class="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-2xl text-sm font-bold outline-none focus:bg-white focus:border-emerald-500">
                </div>
            </div>

            <div class="p-4 bg-emerald-50 rounded-2xl border border-emerald-100 flex items-center justify-between">
                <span class="text-sm font-bold text-emerald-900 text-slate-500">Total chargé lors de l'intervention</span>
                <span class="text-lg font-black text-emerald-600">{{ form.qtt_charge_total }} kg</span>
            </div>

            <div v-if="form.modification_equipement" class="space-y-2 pt-2">
                <label class="text-xs font-black uppercase tracking-widest text-gray-400">
                    <span v-if="form.qtt_charge_total > 0" class="text-red-500 mr-0.5">*</span>Dénomination si changement de fluide
                </label>
                <input type="text" v-model="form.qtt_charge_denomination_fluide_changement" placeholder="ex: R-452A" class="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-2xl text-sm font-bold outline-none focus:bg-white focus:border-emerald-500">
            </div>
        </div>

        <!-- SECTION 5: RÉCUPÉRATION DU FLUIDE -->
        <div class="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 space-y-6">
            <div class="flex items-center gap-2 mb-2">
                <div class="w-1.5 h-6 bg-purple-500 rounded-full"></div>
                <h3 class="font-bold text-lg text-gray-900">Récupération du fluide</h3>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div class="space-y-2">
                    <label class="text-[10px] font-black uppercase tracking-widest text-gray-400">Vers centre traitement (kg)</label>
                    <input type="number" step="0.01" v-model.number="form.qtt_recuperee_traitement" class="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-2xl text-sm font-bold outline-none focus:bg-white focus:border-purple-500">
                </div>
                <div class="space-y-2">
                    <label class="text-[10px] font-black uppercase tracking-widest text-gray-400">Pour réutilisation (kg)</label>
                    <input type="number" step="0.01" v-model.number="form.qtt_recuperee_reutilisation" class="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-2xl text-sm font-bold outline-none focus:bg-white focus:border-purple-500">
                </div>
            </div>

            <div v-if="hasRecovery" class="space-y-6 animate-in slide-in-from-bottom-4 duration-300">
                <div class="p-4 bg-purple-50 rounded-2xl border border-purple-100 grid grid-cols-2 gap-4">
                    <div>
                        <div class="text-[10px] font-black text-purple-400 uppercase tracking-widest mb-1">Total récupéré</div>
                        <div class="text-lg font-black text-purple-600">{{ form.qtt_recuperee_total }} kg</div>
                    </div>
                    <div>
                        <div class="text-[10px] font-black text-purple-400 uppercase tracking-widest mb-1">Catégorie ADR</div>
                        <div class="text-xs font-bold text-purple-900">
                            {{ form.fluide_inflammable ? 'Inflammable (16 05 04*)' : 'Non-inflammable (14 06 01*)' }}
                        </div>
                    </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="space-y-2">
                        <label class="text-xs font-black uppercase tracking-widest text-gray-400">N° BSFF (Trackdéchets)</label>
                        <input type="text" v-model="form.qtt_recuperee_num_bsff" placeholder="BS2026..." class="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-2xl text-sm font-bold outline-none focus:bg-white focus:border-purple-500">
                    </div>
                    <div class="space-y-2">
                        <label class="text-xs font-black uppercase tracking-widest text-gray-400">
                            <span class="text-red-500 mr-0.5">*</span>ID Contenants
                        </label>
                        <input type="text" v-model="form.qtt_recuperee_identification_contenant" placeholder="Bouteille n°12, ..." class="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-2xl text-sm font-bold outline-none focus:bg-white focus:border-purple-500">
                    </div>
                </div>

                <div class="space-y-2">
                    <label class="text-xs font-black uppercase tracking-widest text-gray-400">
                        <span class="text-red-500 mr-0.5">*</span>Installation de destination
                    </label>
                    <textarea v-model="form.destination_recuperation_fluide" rows="2" placeholder="Nom, SIRET et adresse du centre agréé..." class="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-2xl text-sm font-medium focus:bg-white focus:border-purple-500 outline-none"></textarea>
                </div>
            </div>
        </div>

        <!-- OBSERVATIONS -->
        <div class="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 space-y-4">
            <div class="flex items-center gap-2">
                <i class="fas fa-comment-dots text-gray-400"></i>
                <h3 class="font-bold text-gray-900">Observations complémentaires</h3>
            </div>
            <textarea
                v-model="form.observations"
                rows="4"
                placeholder="Remarques, anomalies constatées, informations sur l'installation..."
                class="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-2xl text-sm font-medium focus:bg-white focus:border-gray-200 outline-none transition-all"
            ></textarea>
        </div>

        <!-- ERROR SUMMARY -->
        <div v-if="showErrors && validation.errors.length" class="bg-red-50 rounded-3xl p-6 border border-red-100 space-y-4 animate-in slide-in-from-bottom-4">
            <div class="flex items-center gap-2 text-red-600">
                <i class="fas fa-exclamation-triangle"></i>
                <h3 class="font-black text-sm uppercase tracking-widest">Corriger les erreurs bloquantes</h3>
            </div>
            <ul class="space-y-2">
                <li v-for="(err, i) in validation.errors" :key="i" class="flex items-start gap-2 text-xs font-bold text-red-700">
                    <span class="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 flex-shrink-0"></span>
                    {{ err }}
                </li>
            </ul>
        </div>

        <!-- WARNING SUMMARY -->
        <div v-if="showErrors && validation.warnings.length" class="bg-orange-50 rounded-3xl p-6 border border-orange-100 space-y-4">
            <div class="flex items-center gap-2 text-orange-600">
                <i class="fas fa-info-circle"></i>
                <h3 class="font-black text-sm uppercase tracking-widest">Recommandations</h3>
            </div>
            <ul class="space-y-2">
                <li v-for="(warn, i) in validation.warnings" :key="i" class="flex items-start gap-2 text-xs font-bold text-orange-700">
                    <span class="w-1.5 h-1.5 rounded-full bg-orange-400 mt-1.5 flex-shrink-0"></span>
                    {{ warn }}
                </li>
            </ul>
        </div>

        <div class="max-w-4xl mx-auto flex gap-4">
            <button 
                type="button" 
                @click="validateAndSumbit"
                class="flex-1 py-4 bg-gray-900 text-white rounded-2xl font-black text-sm shadow-xl shadow-slate-200 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3"
                :class="showErrors && validation.errors.length ? 'opacity-50 cursor-not-allowed' : ''"
            >
                <template v-if="loadingSendCerfa">
                    <i class="fas fa-spinner fa-spin text-white"></i>
                    ENVOI EN COURS...
                </template>
                <template v-if="!loadingSendCerfa">
                    <i v-if="!validation.errors.length" class="fas fa-check-circle text-emerald-400"></i>
                    VALIDER LE CERFA
                </template>
            </button>
        </div>
    </div>
</template>

<script>
import fluidesList from '@/config/fluides';

export default {
    name: 'InterventionCerfaForm',
    props: {
        interventionId: { required: true },
    },
    data() {
        return {
            fluides: fluidesList,
            search: '',
            filteredFluides: [],
            showDropdown: false,
            selectedFluide: null,

            form: {
                // Fluide
                fluide_denomination: '',
                fluide_charge_totale_kg: 0,
                fluide_tonnage_eq_co2: 0,
                fluide_prg_manuel: null,

                // Nature intervention
                assemblage_equipement: false,
                mise_en_service: false,
                modification_equipement: false,
                maintenance_equipement: false,
                controle_etancheite_periodique: false,
                controle_etancheite_non_periodique: false,
                demantelement: false,
                intervention_autre: false,
                intervention_autre_precisez: '',

                // Contrôle
                controle_etancheite_identification: '',
                controle_etancheite_date: new Date().toISOString().substr(0, 10),
                systeme_detection_present: false,

                // Catégories réglementaires (utilisées pour la logique mais gardées en structure)
                quantite_fluide_hcfc: '',
                quantite_fluide_hfc_ppc: '',
                quantite_fluide_hfo: '',
                sans_systeme_permanent_detection: '',
                avec_systeme_permanent_detection: '',

                // Fuites
                fuite_oui_non: false,
                fuite_1_localisation: '',
                fuite_1_reparation: 0,
                fuite_2_localisation: '',
                fuite_2_reparation: 0,
                fuite_3_localisation: '',
                fuite_3_reparation: 0,

                // Charge
                qtt_charge_total: 0,
                qtt_charge_fluide_vierge: 0,
                qtt_charge_denomination_fluide_changement: '',
                qtt_charge_fluide_recycle: 0,
                qtt_charge_fluide_regenere: 0,

                // Récupération
                qtt_recuperee_total: 0,
                qtt_recuperee_traitement: 0,
                qtt_recuperee_num_bsff: '',
                qtt_recuperee_reutilisation: 0,
                qtt_recuperee_identification_contenant: '',

                // ADR
                fluide_non_inflammable: false,
                fluide_inflammable: false,

                // Destination
                destination_recuperation_fluide: '',
                observations: '',
                frequence_manuelle: ''
            },

            showErrors: false,
            showManualFrequency: false,

            interventionLabels: {
                assemblage_equipement: 'Assemblage initial',
                mise_en_service: 'Mise en service',
                modification_equipement: 'Modification',
                maintenance_equipement: 'Maintenance',
                controle_etancheite_periodique: 'Contrôle périodique',
                controle_etancheite_non_periodique: 'Contrôle non-périodique',
                demantelement: 'Démantèlement'
            },

            interventionIcons: {
                assemblage_equipement: 'fas fa-tools',
                mise_en_service: 'fas fa-power-off',
                modification_equipement: 'fas fa-cog',
                maintenance_equipement: 'fas fa-wrench',
                controle_etancheite_periodique: 'fas fa-check-double',
                controle_etancheite_non_periodique: 'fas fa-search',
                demantelement: 'fas fa-trash-alt'
            },

            loadingSendCerfa: false
        };
    },

    computed: {
        hasRecovery() {
            return this.form.qtt_recuperee_total > 0;
        },

        leakCheckFrequencyText() {
            if (!this.selectedFluide || !this.form.fluide_charge_totale_kg) return 'Saisir fluide/charge';
            
            const freq = this.form.systeme_detection_present 
                ? this.form.avec_systeme_permanent_detection 
                : this.form.sans_systeme_permanent_detection;
            
            return freq || 'Non déterminé';
        },

        validation() {
            const errors = [];
            const warnings = [];
            const f = this.form;

            // 1. BASE
            if (!f.fluide_denomination || !this.selectedFluide) {
                errors.push("Le fluide frigorigène est obligatoire et doit être sélectionné dans la liste.");
            }
            if (f.fluide_charge_totale_kg === null || f.fluide_charge_totale_kg === undefined || f.fluide_charge_totale_kg === '') {
                errors.push("La charge totale héritée ou initiale de l'équipement est obligatoire.");
            }
            if (this.selectedFluide && f.fluide_charge_totale_kg > 0 && f.fluide_tonnage_eq_co2 === 0) {
                errors.push("Le tonnage équivalent CO₂ n'a pas pu être calculé (PRG manquant ou invalide).");
            }

            // 2. NATURE
            const hasNature = Object.keys(this.interventionLabels).some(key => f[key]) || f.intervention_autre;
            if (!hasNature) {
                errors.push("Au moins une nature d'intervention doit être sélectionnée.");
            }
            if (f.intervention_autre && !f.intervention_autre_precisez) {
                errors.push("Veuillez préciser la nature de l'intervention (champ 'Autre').");
            }
            if (f.intervention_autre_precisez && f.intervention_autre_precisez.length > 30) {
                errors.push("La précision de l'intervention ne doit pas dépasser 30 caractères.");
            }

            // 3. CONTRÔLE
            const isCheckActive = f.controle_etancheite_periodique || f.controle_etancheite_non_periodique;
            if (isCheckActive) {
                if (!f.controle_etancheite_identification) errors.push("L'identification du détecteur est obligatoire pour un contrôle d'étanchéité.");
                if (!f.controle_etancheite_date) errors.push("La date du contrôle d'étanchéité est obligatoire.");
                if (!this.selectedFluide) errors.push("Un fluide doit être sélectionné pour valider un contrôle d'étanchéité.");
            }

            // 4. FRÉQUENCE
            if (this.selectedFluide && f.fluide_charge_totale_kg > 0) {
                const hasFreqCat = f.quantite_fluide_hcfc || f.quantite_fluide_hfc_ppc || f.quantite_fluide_hfo;
                if (!hasFreqCat && !f.frequence_manuelle) {
                    errors.push("La catégorie de fluide n'a pas permis de déterminer la fréquence. Veuillez la saisir manuellement dans l'en-tête.");
                }
                if (this.showManualFrequency && !f.frequence_manuelle) {
                    errors.push("Veuillez saisir la fréquence de contrôle manuellement dans l'en-tête.");
                }
            }

            // 5. FUITES
            if (f.fuite_oui_non) {
                const hasLoc = f.fuite_1_localisation || f.fuite_2_localisation || f.fuite_3_localisation;
                if (!hasLoc) errors.push("Au moins une localisation de fuite est obligatoire si une fuite est déclarée.");
                
                [1, 2, 3].forEach(n => {
                    const loc = f[`fuite_${n}_localisation`];
                    const rep = f[`fuite_${n}_reparation`];

                    if (loc && rep === 0) {
                        errors.push(`Veuillez indiquer l'état de la réparation (Réparée ou À réparer) pour la fuite #${n} identifiée.`);
                    }
                    if (rep === 2 && (!f.observations || f.observations.length < 10)) {
                        warnings.push(`Réparation différée pour la fuite #${n} : il est vivement conseillé de préciser la date prévue de réparation dans les observations.`);
                    }
                });
            }

            // 6. CHARGE
            const sumCharge = Number(((f.qtt_charge_fluide_vierge || 0) + (f.qtt_charge_fluide_recycle || 0) + (f.qtt_charge_fluide_regenere || 0)).toFixed(2));
            if (Math.abs(f.qtt_charge_total - sumCharge) > 0.01) {
                errors.push("La quantité totale chargée est incohérente avec le détail (vierge + recyclé + régénéré).");
            }
            if (f.modification_equipement && f.qtt_charge_total > 0 && !f.qtt_charge_denomination_fluide_changement) {
                errors.push("En cas de modification avec charge, la nouvelle dénomination du fluide est obligatoire.");
            }

            // 7. RÉCUPÉRATION
            if (f.qtt_recuperee_total > 0) {
                if (!f.destination_recuperation_fluide) errors.push("L'installation de destination est obligatoire en cas de récupération de fluide.");
                if (!f.qtt_recuperee_identification_contenant) errors.push("L'identification du contenant est obligatoire en cas de récupération de fluide.");
                if (!f.qtt_recuperee_num_bsff) warnings.push("Récupération effectuée sans numéro BSFF (Trackdéchets) : fortement recommandé pour la traçabilité.");
            }

            // 8. ADR
            if (f.qtt_recuperee_total > 0) {
                if (f.fluide_inflammable && f.fluide_non_inflammable) errors.push("Incohérence ADR : le fluide ne peut pas être à la fois inflammable et non-inflammable.");
                if (!f.fluide_inflammable && !f.fluide_non_inflammable) errors.push("Veuillez sélectionner la catégorie ADR (inflammable ou non-inflammable).");
            }

            // 9. INCOHÉRENCES GLOBALES
            const maxAllowedRecup = (f.fluide_charge_totale_kg || 0) + (f.qtt_charge_total || 0);
            if (f.qtt_recuperee_total > maxAllowedRecup + 0.1) { // 0.1 de marge
                errors.push("La quantité récupérée ne peut pas être supérieure à la charge initiale + charge effectuée.");
            }

            return { errors, warnings };
        }
    },

    watch: {
        // Calcul automatique Tonnage CO2
        'form.fluide_charge_totale_kg': function() { this.computeEqCo2(); },
        'form.fluide_prg_manuel': function() { this.computeEqCo2(); },
        selectedFluide: function() { this.computeEqCo2(); },

        // Sync des champs réglementaires
        'form.fluide_tonnage_eq_co2': function() { this.computeRegulatoryFields(); },
        'form.systeme_detection_present': function() { 
            // On affiche en UI la fréquence selon le détecteur, 
            // mais les deux sont stockés en DB
        },

        // Sommes automatiques Charge
        'form.qtt_charge_fluide_vierge': function() { this.computeChargeTotal(); },
        'form.qtt_charge_fluide_recycle': function() { this.computeChargeTotal(); },
        'form.qtt_charge_fluide_regenere': function() { this.computeChargeTotal(); },

        // Sommes automatiques Récupération
        'form.qtt_recuperee_traitement': function() { this.computeRecuperationTotal(); },
        'form.qtt_recuperee_reutilisation': function() { this.computeRecuperationTotal(); }
    },

    methods: {
        filterFluides() {
            const s = this.search.toLowerCase().trim();
            if (!s) {
                this.filteredFluides = [];
                return;
            }
            this.filteredFluides = this.fluides.filter(f =>
                f.code.toLowerCase().includes(s) ||
                f.nom.toLowerCase().includes(s)
            ).slice(0, 10);
        },

        selectFluide(f) {
            this.selectedFluide = f;
            this.form.fluide_denomination = f.code;
            this.search = f.code;
            this.showDropdown = false;

            // ADR / Inflammabilité
            this.form.fluide_inflammable = !!f.inflammable;
            this.form.fluide_non_inflammable = !f.inflammable;

            this.computeEqCo2();
        },

        computeEqCo2() {
            if (!this.selectedFluide || !this.form.fluide_charge_totale_kg) {
                this.form.fluide_tonnage_eq_co2 = 0;
                return;
            }
            const prg = this.selectedFluide.prg !== null ? this.selectedFluide.prg : (this.form.fluide_prg_manuel || 0);
            const eq = (this.form.fluide_charge_totale_kg * prg) / 1000;
            this.form.fluide_tonnage_eq_co2 = Number(eq.toFixed(3));
        },

        computeRegulatoryFields() {
            if (!this.selectedFluide) return;

            const type = this.selectedFluide.type;
            const charge = this.form.fluide_charge_totale_kg || 0;
            const tCo2 = this.form.fluide_tonnage_eq_co2 || 0;

            // Reset
            this.form.quantite_fluide_hcfc = '';
            this.form.quantite_fluide_hfc_ppc = '';
            this.form.quantite_fluide_hfo = '';

            // 1. Catégories de quantité
            if (type === 'HCFC') {
                if (charge < 2) this.form.quantite_fluide_hcfc = '0';
                else if (charge < 30) this.form.quantite_fluide_hcfc = '1';
                else if (charge < 300) this.form.quantite_fluide_hcfc = '2';
                else this.form.quantite_fluide_hcfc = '3';
            } 
            else if (['HFC', 'PFC', 'Mélange'].includes(type) || this.selectedFluide.code.startsWith('R4')) {
                if (tCo2 < 5) this.form.quantite_fluide_hfc_ppc = '0';
                else if (tCo2 < 50) this.form.quantite_fluide_hfc_ppc = '1';
                else if (tCo2 < 500) this.form.quantite_fluide_hfc_ppc = '2';
                else this.form.quantite_fluide_hfc_ppc = '3';
            } 
            else if (type === 'HFO') {
                if (tCo2 < 5) this.form.quantite_fluide_hfo = '0';
                else if (tCo2 < 50) this.form.quantite_fluide_hfo = '1';
                else if (tCo2 < 500) this.form.quantite_fluide_hfo = '2';
                else this.form.quantite_fluide_hfo = '3';
            }
            else {
                // Cas non standard : on laisse l'utilisateur saisir la fréquence manuellement
                this.form.quantite_fluide_hfc_ppc = 'Autre (seuil manuel)';
            }

            // 2. Fréquences
            this.form.sans_systeme_permanent_detection = this.calculateFrequency(false);
            this.form.avec_systeme_permanent_detection = this.calculateFrequency(true);

            // Toggle manual frequency if calculation fails
            this.showManualFrequency = !this.form.sans_systeme_permanent_detection || this.form.sans_systeme_permanent_detection === 'Non défini';
        },

        calculateFrequency(withDetector) {
            if (!this.selectedFluide || !this.form.fluide_charge_totale_kg) return '';

            const type = this.selectedFluide.type;
            const charge = this.form.fluide_charge_totale_kg;
            const tCo2 = this.form.fluide_tonnage_eq_co2;

            if (['HFC', 'HFO', 'PFC', 'Mélange'].includes(type) || this.selectedFluide.code.startsWith('R4')) {
                if (tCo2 < 5) return 'Non obligatoire';
                if (tCo2 < 50) return withDetector ? '24 mois' : '12 mois';
                if (tCo2 < 500) return withDetector ? '12 mois' : '6 mois';
                return withDetector ? '6 mois' : '3 mois';
            }

            if (type === 'HCFC') {
                if (charge < 2) return 'Non obligatoire';
                if (charge < 30) return '12 mois';
                if (charge < 300) return '6 mois';
                return '3 mois';
            }

            return 'Non défini';
        },

        computeChargeTotal() {
            this.form.qtt_charge_total = Number((
                (this.form.qtt_charge_fluide_vierge || 0) +
                (this.form.qtt_charge_fluide_recycle || 0) +
                (this.form.qtt_charge_fluide_regenere || 0)
            ).toFixed(2));
        },

        computeRecuperationTotal() {
            this.form.qtt_recuperee_total = Number((
                (this.form.qtt_recuperee_traitement || 0) +
                (this.form.qtt_recuperee_reutilisation || 0)
            ).toFixed(2));
        },

        async validateAndSumbit() {
            this.showErrors = true;
            if (this.validation.errors.length > 0) {
                return;
            }
            
            // Si OK, on émet l'événement de validation vers le parent
            let endData = {...this.form}
            endData.intervention_id = this.interventionId;
            // Convertir les booléens en 1/0
            Object.keys(endData).forEach(key => {
                if (typeof endData[key] === 'boolean') {
                    endData[key] = endData[key] ? 1 : 0;
                }
            });

            this.loadingSendCerfa = true
            let cerfaUrl = await this.$request.post('/cerfa/create', endData)
            this.loadingSendCerfa = false

            this.$emit('created', cerfaUrl);
            // this.$toast.success("CERFA validé avec succès");
        }
    },

    mounted() {
        // Fermer le dropdown au clic extérieur
        document.addEventListener('click', (e) => {
            if (this.$el && !this.$el.contains(e.target)) this.showDropdown = false;
        });
    }
};
</script>

<style scoped>
@keyframes in {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}

.animate-in {
    animation: in 0.3s ease-out forwards;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
}

input[type=number] {
    -webkit-appearance: textfield;
    -moz-appearance: textfield;
    appearance: textfield;
}
</style>
